import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { StorePicker } from '../../components/StorePicker';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { usePersistedState } from '../../hooks/usePersistedState';
import { ApiService } from '../../services/api';
import { ControlPogResetModRow, Store } from '../../types';
import { PogResetMappingActions } from './components/PogResetMappingActions';
import { usePogResetMappingRun } from './hooks/usePogResetMappingRun';

type SignedOffFilter = '' | 'yes' | 'no';

const isSignedOffFilter = (value: unknown): value is SignedOffFilter =>
  value === '' || value === 'yes' || value === 'no';

type SortKey =
  | 'tagName'
  | 'pogName'
  | 'modNumber'
  | 'modCount'
  | 'score'
  | 'coverage'
  | 'scannedCount'
  | 'kind'
  | 'signedOff';

// Ranks the "Kind" chips as they read on screen, so a descending sort puts
// scan-confirmed rows on top rather than ordering by the raw kind string.
const KIND_SORT_RANK = (mod: ControlPogResetModRow): number => {
  const suggested = mod.suggested;
  if (!suggested) {
    // Assigned-only mods (live POG, no open task) rank above wholly untouched ones.
    return mod.assignedPogNumber ? 0 : -1;
  }

  if (suggested.kind === 'proposed') {
    return suggested.method === 'scan' ? 3 : 2;
  }

  return 1;
};

// String columns compare case-insensitively; numeric ones fall back to -1 so mods
// without a suggestion sink to the bottom in descending order.
const SORT_VALUES: Record<SortKey, (mod: ControlPogResetModRow) => string | number> = {
  tagName: (mod) => (mod.tagName ?? '').toLowerCase(),
  pogName: (mod) => (mod.suggested?.pogName ?? '').toLowerCase(),
  modNumber: (mod) => mod.suggested?.modNumber ?? -1,
  modCount: (mod) => mod.suggested?.modCount ?? -1,
  score: (mod) => mod.suggested?.score ?? -1,
  coverage: (mod) => mod.suggested?.coverage ?? -1,
  scannedCount: (mod) => mod.suggested?.scannedCount ?? -1,
  kind: KIND_SORT_RANK,
  signedOff: (mod) => (mod.isSignedOff ? 1 : 0),
};

const SORTABLE_COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'tagName', label: 'Tag' },
  { key: 'pogName', label: 'Suggested planogram' },
  { key: 'modNumber', label: 'POG mod #' },
  { key: 'modCount', label: 'POG mods' },
  { key: 'score', label: 'Score' },
  { key: 'coverage', label: 'Coverage' },
  { key: 'scannedCount', label: 'Scans' },
  { key: 'kind', label: 'Kind' },
  { key: 'signedOff', label: 'Signed off' },
];

export const PogResetModsPage: React.FC = () => {
  const { storeName } = useParams<{ storeName: string }>();
  const navigate = useNavigate();
  const { token } = useAuthContext();
  const { toggleLoading, showErrorToast } = useNotificationContext();

  const [mods, setMods] = useState<ControlPogResetModRow[]>([]);
  const [store, setStore] = useState<Store | null>(null);
  // The picker stays within the current store's chain — cross-chain jumps are not a workflow
  // this page serves.
  const [chainStores, setChainStores] = useState<Store[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [hideUnsuggested, setHideUnsuggested] = usePersistedState<boolean>(
    'pogReset.mods.hideUnsuggested',
    false,
    (value): value is boolean => typeof value === 'boolean'
  );
  // '' = all; 'yes'/'no' filter on the aisle's technician sign-off. Defaults to 'yes':
  // mods on un-signed-off aisles aren't ready to map, so they're noise by default.
  const [signedOffFilter, setSignedOffFilter] = usePersistedState<SignedOffFilter>(
    'pogReset.mods.signedOffFilter',
    'yes',
    isSignedOffFilter
  );
  // Scan-confirmed suggestions are the trustworthy ones — surface them first by default.
  const [sort, setSort] = useState<{ key: SortKey; dir: 'desc' | 'asc' } | null>({
    key: 'kind',
    dir: 'desc',
  });
  const [hasUnprocessedOpenTasks, setHasUnprocessedOpenTasks] = useState(false);
  const [lastComputedAt, setLastComputedAt] = useState<string | null>(null);

  const mappingRun = usePogResetMappingRun({
    storeName,
    onRunComplete: () => loadMods(true),
  });

  const loadMods = async (silent = false) => {
    if (!storeName) {
      return;
    }

    if (!silent) {
      toggleLoading(true);
      setLoaded(false);
    }

    try {
      // The mods endpoint carries neither task statuses nor calculation timestamps,
      // so the task list is fetched alongside it to drive the run controls.
      const [modsResponse, tasksResponse, stores] = await Promise.all([
        ApiService.fetchPogResetMods(token, storeName),
        ApiService.fetchPogResetTasks(token, storeName, 'open'),
        // Only for the header label and store picker — the table must still render if it fails.
        ApiService.fetchStores(token).catch(() => null),
      ]);
      setMods(modsResponse.mods);
      const currentStore = stores?.find((entry) => entry.storeName === storeName) ?? null;
      setStore(currentStore);
      setChainStores(
        currentStore ? (stores ?? []).filter((s) => s.chainId === currentStore.chainId) : []
      );
      setHasUnprocessedOpenTasks(
        tasksResponse.tasks.some((task) => task.status === 'open' && !task.mapping)
      );
      const timestamps = tasksResponse.tasks
        .map((task) => task.mapping?.computedAt)
        .filter((value): value is string => Boolean(value));
      setLastComputedAt(
        timestamps.length > 0
          ? timestamps.reduce((latest, value) => (value > latest ? value : latest))
          : null
      );
      // Picks up a run already in flight, so a mid-run reload keeps polling.
      mappingRun.syncActiveRun(tasksResponse.activeRun);
      setLoaded(true);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error loading mod suggestions');
    } finally {
      if (!silent) {
        toggleLoading(false);
      }
    }
  };

  useEffect(() => {
    setMods([]);
    loadMods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, storeName]);

  const filteredMods = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matching = mods.filter((mod) => {
      if (hideUnsuggested && !mod.suggested) return false;
      if (signedOffFilter === 'yes' && !mod.isSignedOff) return false;
      if (signedOffFilter === 'no' && mod.isSignedOff) return false;
      if (!query) return true;
      return (
        (mod.tagName ?? '').toLowerCase().includes(query) ||
        (mod.suggested?.pogName ?? '').toLowerCase().includes(query) ||
        (mod.suggested?.pogNumber ?? '').toLowerCase().includes(query)
      );
    });
    if (!sort) {
      return matching;
    }

    const getValue = SORT_VALUES[sort.key];
    return [...matching].sort((a, b) => {
      const valueA = getValue(a);
      const valueB = getValue(b);
      const comparison =
        typeof valueA === 'string' && typeof valueB === 'string'
          ? valueA.localeCompare(valueB)
          : Number(valueA) - Number(valueB);
      return sort.dir === 'desc' ? -comparison : comparison;
    });
  }, [mods, search, hideUnsuggested, signedOffFilter, sort]);

  const cycleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev?.key !== key) {
        return { key, dir: 'desc' };
      }

      return prev.dir === 'desc' ? { key, dir: 'asc' } : null;
    });
  };

  const sortIndicator = (key: SortKey) =>
    sort?.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '';

  const suggestedCount = mods.filter((mod) => mod.suggested).length;

  const taskIdForMod = (mod: ControlPogResetModRow): number | null =>
    mod.suggested?.taskId ?? mod.taskId;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Link
            to={`/stores/${storeName}/pog-reset`}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded-md"
            title="Back to reset tasks"
          >
            <ArrowBackIcon />
          </Link>
          <div>
            <h1 className="text-2xl font-bold mb-0.5">
              Mod tags — planogram suggestions
              <span className="ml-2 text-base font-normal text-gray-600">
                {store?.legibleName ?? storeName}
              </span>
            </h1>
            <p className="text-sm text-gray-600">
              Every camera-covered mod and its best match from open reset tasks ({suggestedCount}/
              {mods.length} suggested)
              {signedOffFilter === 'yes' && mods.length > 0 && (
                <span className="text-gray-500"> · signed-off aisles only</span>
              )}
            </p>
          </div>
          <div className="ml-auto">
            <StorePicker
              stores={chainStores}
              current={store}
              onSelect={(s) => navigate(`/stores/${s.storeName}/pog-reset/mods`)}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex items-center gap-4 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by tag or planogram…"
            className="w-64 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={hideUnsuggested}
              onChange={(e) => setHideUnsuggested(e.target.checked)}
            />
            Hide mods without a suggestion
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            Signed off
            <select
              value={signedOffFilter}
              onChange={(e) => setSignedOffFilter(e.target.value as SignedOffFilter)}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Whether a technician confirmed the aisle's camera installation is complete"
            >
              <option value="">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <div className="flex-1" />
          {lastComputedAt && !mappingRun.isRunning && (
            <span
              className="text-xs text-gray-500 whitespace-nowrap"
              title="Most recent mapping calculation across the open reset tasks"
            >
              Last calculated: {new Date(lastComputedAt).toLocaleString()}
            </span>
          )}
          <PogResetMappingActions
            run={mappingRun}
            showGenerate={hasUnprocessedOpenTasks}
            disabled={!storeName}
          />
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {SORTABLE_COLUMNS.map(({ key, label }) => (
                  <th key={key} className="px-4 py-2 text-left font-medium text-gray-500">
                    <button onClick={() => cycleSort(key)} className="hover:text-gray-900">
                      {label}
                      {sortIndicator(key)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMods.map((mod) => {
                const taskId = taskIdForMod(mod);
                return (
                  <tr
                    key={mod.modCoverageId}
                    onClick={() =>
                      taskId != null &&
                      navigate(`/stores/${storeName}/pog-reset/${taskId}`, {
                        state: {
                          taskIds: [
                            ...new Set(
                              filteredMods
                                .map((m) => taskIdForMod(m))
                                .filter((id): id is number => id != null)
                            ),
                          ],
                          backTo: `/stores/${storeName}/pog-reset/mods`,
                        },
                      })
                    }
                    className={`hover:bg-gray-50 ${taskId != null ? 'cursor-pointer' : ''}`}
                  >
                    <td className="px-4 py-2 font-mono font-medium whitespace-nowrap">
                      {mod.tagName ?? '—'}
                      {mod.isTrackingExcluded && (
                        <span
                          className="ml-2 px-2 py-0.5 rounded-full text-xs font-sans font-medium bg-amber-100 text-amber-800"
                          title="This mod is excluded from tracking (processing off) — suggestions may come from stale images"
                        >
                          tracking off
                        </span>
                      )}
                      {!mod.isComplianceEnabled && (
                        <span
                          className="ml-2 px-2 py-0.5 rounded-full text-xs font-sans font-medium bg-gray-200 text-gray-600"
                          title="Product-recognition compliance is disabled for this mod — no inference data exists, so it cannot get a suggestion"
                        >
                          PR off
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {mod.suggested ? (
                        <>
                          <div className="font-medium">{mod.suggested.pogName ?? '—'}</div>
                          <div className="text-xs text-gray-500">
                            {mod.suggested.pogNumber ?? ''}
                          </div>
                        </>
                      ) : mod.assignedPogNumber ? (
                        // No open-task suggestion, but the mod already serves a live POG —
                        // show it (dimmed) so the 'assigned' chip isn't a bare label.
                        <>
                          <div className="text-gray-600">{mod.assignedPogName ?? '—'}</div>
                          <div className="text-xs text-gray-500">{mod.assignedPogNumber}</div>
                        </>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {mod.suggested?.modNumber ??
                        (mod.assignedModNumber != null ? (
                          <span className="text-gray-600">{mod.assignedModNumber}</span>
                        ) : (
                          ''
                        ))}
                    </td>
                    <td className="px-4 py-2">
                      {mod.suggested?.modCount ??
                        (mod.assignedModCount != null ? (
                          <span className="text-gray-600">{mod.assignedModCount}</span>
                        ) : (
                          ''
                        ))}
                    </td>
                    <td className="px-4 py-2">
                      {mod.suggested?.score != null
                        ? `${Math.round(mod.suggested.score * 100)}%`
                        : ''}
                    </td>
                    <td className="px-4 py-2">
                      {mod.suggested?.coverage != null
                        ? `${Math.round(mod.suggested.coverage * 100)}%`
                        : ''}
                    </td>
                    {/* Barcodes scanned against this mod at install. The score is
                        matched/scanned, so depth is what tells 1-of-1 from 3-of-3. */}
                    <td className="px-4 py-2">
                      {mod.suggested?.scannedCount != null ? (
                        <span
                          className={
                            mod.suggested.scannedCount >= 3 ? 'text-gray-800' : 'text-amber-700'
                          }
                          title={
                            mod.suggested.scannedCount >= 3
                              ? `${mod.suggested.scannedCount} barcodes scanned at install`
                              : `Only ${mod.suggested.scannedCount} barcode(s) scanned — a perfect score on a shallow scan is weak evidence`
                          }
                        >
                          {mod.suggested.scannedCount}
                        </span>
                      ) : (
                        <span className="text-gray-400" title="Not scanned at install">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        {mod.suggested && (
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              mod.suggested.kind === 'proposed' && mod.suggested.method === 'scan'
                                ? 'bg-violet-100 text-violet-800'
                                : mod.suggested.kind === 'proposed'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-600'
                            }`}
                            title={
                              mod.suggested.kind === 'proposed' && mod.suggested.method === 'scan'
                                ? 'Confirmed by field scan during camera installation'
                                : undefined
                            }
                          >
                            {mod.suggested.kind === 'proposed' && mod.suggested.method === 'scan'
                              ? 'scanned'
                              : mod.suggested.kind}
                          </span>
                        )}
                        {/* Independent of any open task: this mod already serves a live POG. */}
                        {mod.assignedPogNumber && (
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                            title={
                              `Currently assigned to an active planogram: ${
                                mod.assignedPogName ?? ''
                              } (${mod.assignedPogNumber})` +
                              (mod.assignedModNumber != null
                                ? ` — mod ${mod.assignedModNumber}${
                                    mod.assignedModCount != null
                                      ? ` of ${mod.assignedModCount}`
                                      : ''
                                  }`
                                : '') +
                              (mod.assignedIsMirrored ? ' (mirrored)' : '') +
                              (mod.assignedAt
                                ? ` · assigned ${new Date(mod.assignedAt).toLocaleString()}`
                                : '')
                            }
                          >
                            assigned
                          </span>
                        )}
                        {!mod.suggested && !mod.assignedPogNumber && (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {mod.isSignedOff ? (
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 cursor-help"
                          title={`Aisle installation signed off${
                            mod.signedOffBy ? ` by ${mod.signedOffBy}` : ''
                          }${
                            mod.signedOffAt
                              ? ` on ${new Date(mod.signedOffAt).toLocaleString()}`
                              : ''
                          }`}
                        >
                          yes
                        </span>
                      ) : (
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 cursor-help"
                          title="No technician sign-off for this mod's aisle yet"
                        >
                          no
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {loaded && filteredMods.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No mods match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
